import React, { useCallback, useEffect, useState } from "react";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useOrderHistoryApi } from "@danishagro/shared/src/hooks/api/useOrderHistoryApi.hook";
import { DA_Accordion } from "@danishagro/shared/src/components/molecules/Accordion/Accordion.component";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { format } from "date-fns";
import { DA_OrderHistory } from "@danishagro/shared/src/interfaces/orderHistory.interface";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Badge } from "@danishagro/shared/src/components/organisms/SiteHeader/Badge/Badge.component";
import Skeleton from "react-loading-skeleton";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import {
    DA_EmptyState,
    DA_EmptyStateTypes,
} from "@danishagro/shared/src/components/molecules/EmptyState/EmptyState.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_OrderHistoryResponse } from "@danishagro/shared/src/interfaces/orderHistory.interface";
import { OrderDetailsTimePicker } from "../components/OrderDetailsTimePicker/OrderDetailsTimePicker.component";
import { OrderDetails } from "../components/OrderDetails/OrderDetails.component";
import { Pagination } from "../components/Pagination/Pagination.component";
import { SortOrder } from "../components/SortOrder/SortOrder.component";
import S from "./OrderHistoryV1.module.scss";

interface V1Props {
    parentVersion?: string;
}

const V1 = (_props: V1Props) => {
    const { getDictionaryString } = useTranslations();
    const { getOrderHistory } = useOrderHistoryApi();
    const { currentCulture } = useAppData();
    const { isMobileOrTablet } = useScreen();
    const [orderHistory, setOrderHistory] = useState<DA_OrderHistory[]>([]);
    const { showOverlay, closeOverlay } = useDynamicOverlay();
    const [fromDate, setFromDate] = useState<string>(undefined);
    const [toDate, setToDate] = useState<string>(undefined);

    const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
    const [pageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [awaitingResponse, setAwaitingResponse] = useState(false);

    const fetchHistory = useCallback(
        async (parameters?: { fromDate?: string; toDate?: string }) => {
            setAwaitingResponse(true);
            try {
                const params = {
                    ...parameters,
                    sortorder: sortOrder,
                    pagesize: String(pageSize),
                    page: String(pageNumber),
                    version: "2",
                };
                const history = (await getOrderHistory(params)) as DA_OrderHistoryResponse;
                setOrderHistory(history.items);
                setTotalCount(history.totalCount);
            } catch (error) {
                console.error(error);
            } finally {
                setAwaitingResponse(false);
            }
        },
        [getOrderHistory, pageNumber, pageSize, sortOrder]
    );

    useEffect(() => {
        if (fromDate && toDate) {
            fetchHistory({ fromDate, toDate });
            closeOverlay();
        } else {
            fetchHistory();
        }
    }, [fromDate, toDate, fetchHistory, isMobileOrTablet, closeOverlay]);

    const onClickFilter = useCallback(() => {
        showOverlay({
            id: "order-history-filter-overlay",
            title: getDictionaryString("profile.orderHistory.timePeriod"),
            content: (
                <div className={S.overlayContent}>
                    <OrderDetailsTimePicker
                        fromDate={fromDate}
                        toDate={toDate}
                        setFromDate={setFromDate}
                        setToDate={setToDate}
                        disabled={awaitingResponse}
                    />
                    <SortOrder
                        sortOrder={sortOrder}
                        onChangeSortOrder={(sortOrder: string) => {
                            setSortOrder(sortOrder as "ASC" | "DESC");
                            closeOverlay();
                        }}
                    />
                </div>
            ),
        });
    }, [
        awaitingResponse,
        closeOverlay,
        fromDate,
        getDictionaryString,
        showOverlay,
        sortOrder,
        toDate,
    ]);

    return (
        <DA_Container noTopPadding>
            {isMobileOrTablet ? (
                <div className={S.filterButton}>
                    <DA_Button
                        small
                        onClick={onClickFilter}
                        title={getDictionaryString("profile.orderHistory.filter")}
                        isGhost
                        disabled={awaitingResponse}
                    >
                        {(fromDate || toDate) && <DA_Badge text={orderHistory.length} />}
                    </DA_Button>
                </div>
            ) : (
                <div className={S.toolbar}>
                    <div>
                        <OrderDetailsTimePicker
                            fromDate={fromDate}
                            toDate={toDate}
                            setFromDate={setFromDate}
                            setToDate={setToDate}
                            disabled={awaitingResponse}
                        />
                    </div>
                    <SortOrder
                        sortOrder={sortOrder}
                        onChangeSortOrder={(sortOrder: string) => {
                            setSortOrder(sortOrder as "ASC" | "DESC");
                        }}
                    />
                </div>
            )}

            {(orderHistory.length === 0 && fromDate && toDate && !awaitingResponse) ||
            (orderHistory.length === 0 && !awaitingResponse) ? (
                <DA_EmptyState type={DA_EmptyStateTypes.NoResults} />
            ) : (
                orderHistory.map((order, index) => {
                    const priceFormatted = formatNumber(
                        order.data.prices?.totalPriceWithVat ?? 0,
                        currentCulture,
                        {
                            decimals: 2,
                        }
                    );

                    const orderDateFormatted = order.updatedDate
                        ? format(new Date(order.updatedDate), "dd.MM.yyyy")
                        : "";

                    return (
                        <DA_Accordion
                            key={index}
                            title={
                                <div className={S.accordionTitleWrapper}>
                                    <div
                                        className={cn(
                                            S.accordionTitleDate,
                                            awaitingResponse && S.accordionTitleDateLoading
                                        )}
                                    >
                                        {awaitingResponse ? (
                                            <Skeleton width="30%" />
                                        ) : (
                                            <span>{orderDateFormatted}</span>
                                        )}

                                        {awaitingResponse ? (
                                            <Skeleton width="60%" />
                                        ) : (
                                            <b>#{order.shortId}</b>
                                        )}
                                    </div>
                                    <span
                                        className={cn(
                                            S.accordionTotal,
                                            awaitingResponse && S.accordionTotalLoading
                                        )}
                                    >
                                        {awaitingResponse ? (
                                            <Skeleton width="100%" />
                                        ) : (
                                            getDictionaryString("profile.orderHistory.total", {
                                                total: priceFormatted,
                                            })
                                        )}
                                    </span>
                                </div>
                            }
                            showToggleIcons
                            animation
                            showLargeArrow
                            showSmallHeader
                            noPaddingHeader
                            noPaddingContent
                            headerFullWidth
                            className={S.accordion}
                        >
                            {awaitingResponse ? (
                                <div className={S.spinner}>
                                    <DA_Spinner />
                                </div>
                            ) : (
                                <OrderDetails {...order} />
                            )}
                        </DA_Accordion>
                    );
                })
            )}

            {totalCount > pageSize && (
                <div className={S.paginationBottom}>
                    <Pagination
                        page={pageNumber}
                        pageSize={pageSize}
                        totalItems={totalCount}
                        onChangePage={(pageNumber: number) => {
                            setPageNumber(pageNumber);
                        }}
                    />
                </div>
            )}
        </DA_Container>
    );
};

export default V1;
